import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, Check, Trash2, Plus, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/shared/lib/api";
import { useAuth } from "@/shared/context/AuthContext";
import SubPageShell from "@/apps/rider/components/SubPageShell";
import AddCardSheet from "@/apps/rider/components/AddCardSheet";

/**
 * /payment-methods — list, set default, remove cards on the rider's
 * Stripe customer. Adding a new card deep-links into the Stripe Billing
 * Portal where Stripe owns the secure card-entry form (PCI-compliant).
 */
export default function PaymentMethods() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  // In-app Add Card sheet — opens a Stripe Elements modal that hits
  // /api/me/payment-methods/setup-intent. The legacy Billing Portal
  // redirect is retained as a manual fallback (see openPortal) and
  // surfaced as a small secondary link below the primary Add CTA.
  const [addOpen, setAddOpen] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/me/payment-methods");
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load payment methods");
      setData({ methods: [], has_customer: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    // Defer the load() call to avoid the react-hooks/set-state-in-effect
    // rule which flags synchronous setState during effect bodies. The
    // queueMicrotask hop has no perceptible UX impact and keeps the
    // linter satisfied without disabling rules globally.
    queueMicrotask(load);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const setDefault = async (pm) => {
    setBusyId(pm.id);
    try {
      await api.post(`/me/payment-methods/${pm.id}/default`);
      toast.success("Default card updated");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update default");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (pm) => {
    if (!window.confirm(`Remove ${brandLabel(pm.brand)} ending in ${pm.last4}?`)) return;
    setBusyId(pm.id);
    try {
      await api.delete(`/me/payment-methods/${pm.id}`);
      toast.success("Card removed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not remove card");
    } finally {
      setBusyId(null);
    }
  };

  // After a successful in-app card add: refresh the list. If this was
  // the rider's first card (no prior default), promote it to default
  // automatically so the next booking lands on it without a tap.
  const handleCardAdded = async (newPmId) => {
    await load();
    try {
      const { data: fresh } = await api.get("/me/payment-methods");
      const onlyCard = fresh?.methods?.length === 1 && fresh?.methods?.[0];
      const noneDefault = fresh?.methods?.every((m) => !m.is_default);
      if (newPmId && onlyCard && noneDefault) {
        await api.post(`/me/payment-methods/${newPmId}/default`);
        await load();
      }
    } catch (_) { /* non-fatal — list is already refreshed */ }
  };

  const openPortal = async () => {
    setOpeningPortal(true);
    try {
      const { data: portal } = await api.post("/billing/portal", {
        origin_url: window.location.origin,
      });
      window.location.href = portal.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not open billing portal");
      setOpeningPortal(false);
    }
  };

  if (authLoading) return null;
  if (!user) return <SignedOut />;

  return (
    <>
      <SubPageShell
        eyebrow="Wallet"
        title="Payment methods"
        subtitle="Cards saved with Link. Set the default that's charged for your scheduled rides."
        back="/account"
        backLabel="Account"
        testId="payment-methods-page"
      >
        {loading ? (
          <Loading />
        ) : !data?.has_customer || data.methods.length === 0 ? (
          <EmptyState onAdd={() => setAddOpen(true)} />
        ) : (
          <>
            <ul className="space-y-2.5" data-testid="payment-methods-list">
              {data.methods.map((pm) => (
                <li
                  key={pm.id}
                  className="bg-white border hairline rounded-2xl p-4 flex items-start gap-3"
                  data-testid={`payment-method-${pm.id}`}
                >
                  <span className="w-10 h-10 inline-flex items-center justify-center bg-[#FCEADE] text-[#ed6500] rounded-xl shrink-0">
                    <CreditCard size={17} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#0A1128] text-[15px] leading-tight">
                      {brandLabel(pm.brand)} ···· {pm.last4}
                      {pm.is_default && (
                        <span
                          className="ml-2 inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase font-semibold text-[#ed6500]"
                          data-testid="payment-method-default-badge"
                        >
                          <Check size={10} strokeWidth={2.4} /> Default
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#515E7B] mt-0.5">
                      Expires {String(pm.exp_month).padStart(2, "0")}/{String(pm.exp_year).slice(-2)}
                      {pm.funding && pm.funding !== "card" && ` · ${capitalize(pm.funding)}`}
                    </div>
                    <div className="flex items-center gap-3 mt-3">
                      {!pm.is_default && (
                        <button
                          type="button"
                          onClick={() => setDefault(pm)}
                          disabled={busyId === pm.id}
                          className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#0A1128] hover:text-[#ed6500] disabled:opacity-50"
                          data-testid={`payment-method-${pm.id}-default`}
                        >
                          {busyId === pm.id ? "Updating…" : "Set as default"}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => remove(pm)}
                        disabled={busyId === pm.id}
                        className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#515E7B] hover:text-[#ed6500] disabled:opacity-50 inline-flex items-center gap-1"
                        data-testid={`payment-method-${pm.id}-remove`}
                      >
                        <Trash2 size={11} /> Remove
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="mt-5 w-full bg-[#0A1128] text-[#F9F7F4] px-5 py-4 rounded-2xl flex items-center justify-between hover:bg-[#0A1128]/95"
              data-testid="payment-methods-add"
            >
              <span className="inline-flex items-center gap-3">
                <span className="w-9 h-9 inline-flex items-center justify-center bg-white/10 text-[#ed6500] rounded-xl">
                  <Plus size={16} />
                </span>
                <span className="font-semibold">Add a new card</span>
              </span>
            </button>
            <p className="text-[11px] text-[#515E7B] mt-2 px-1">
              We never see your card number — Stripe handles entry directly.{" "}
              <button
                type="button"
                onClick={openPortal}
                disabled={openingPortal}
                className="text-[#0A1128] hover:text-[#ed6500] underline underline-offset-2 disabled:opacity-50 inline-flex items-center gap-1"
                data-testid="payment-methods-portal-link"
              >
                {openingPortal ? "Opening…" : "Manage in Stripe"}
                <ExternalLink size={10} />
              </button>
            </p>
          </>
        )}
      </SubPageShell>

      <AddCardSheet
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSuccess={handleCardAdded}
      />
    </>
  );
}

function brandLabel(brand) {
  if (!brand) return "Card";
  const map = {
    visa: "Visa",
    mastercard: "Mastercard",
    amex: "American Express",
    discover: "Discover",
    diners: "Diners Club",
    jcb: "JCB",
    unionpay: "UnionPay",
  };
  return map[brand.toLowerCase()] || capitalize(brand);
}
function capitalize(s) {
  return (s || "").charAt(0).toUpperCase() + (s || "").slice(1);
}

function Loading() {
  return (
    <div className="flex items-center gap-2 text-[12px] text-[#515E7B] py-12 justify-center">
      <Loader2 size={14} className="animate-spin" /> Loading saved cards…
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="bg-white border hairline rounded-2xl p-6 text-center" data-testid="payment-methods-empty">
      <div className="w-12 h-12 mx-auto bg-[#FCEADE] text-[#ed6500] rounded-2xl inline-flex items-center justify-center">
        <CreditCard size={20} />
      </div>
      <div className="font-serif text-xl text-[#0A1128] mt-3">No saved cards yet</div>
      <p className="text-sm text-[#515E7B] mt-2 max-w-sm mx-auto">
        Add a card now and we&apos;ll use it automatically the next time you schedule a ride.
      </p>
      <button
        type="button"
        onClick={onAdd}
        className="mt-5 bg-[#0A1128] text-[#F9F7F4] px-5 py-3 rounded-xl text-[12px] tracking-[0.18em] uppercase font-semibold inline-flex items-center gap-2"
        data-testid="payment-methods-empty-add"
      >
        <Plus size={14} />
        Add a card
      </button>
    </div>
  );
}

function SignedOut() {
  return (
    <SubPageShell
      eyebrow="Wallet"
      title="Payment methods"
      subtitle="Sign in to manage saved cards."
      back="/account"
      backLabel="Account"
      testId="payment-methods-page"
    >
      <Link
        to="/login"
        className="inline-flex items-center gap-2 bg-[#0A1128] text-[#F9F7F4] px-5 py-3 rounded-xl text-[12px] tracking-[0.18em] uppercase font-semibold"
      >
        Sign in
      </Link>
    </SubPageShell>
  );
}
